import axios from "axios";
import {
  API_BASE_MOLECULAR_MEDICINE_URL,
  API_MOLECULAR_MEDICINE_LIST,
  API_MOLECULAR_MEDICINE_DETAILS,
  API_MOLECULAR_MEDICINE_CREATE,
  API_MOLECULAR_MEDICINE_UPDATE,
  API_MOLECULAR_MEDICINE_DELETE,
  API_MOLECULAR_MEDICINE_STATS,
  API_MOLECULAR_MEDICINE_URGENT,
  API_MOLECULAR_MEDICINE_SEARCH_GENE,
  API_MOLECULAR_MEDICINE_BY_PATIENT,
  API_MOLECULAR_MEDICINE_STATUS,
  API_MOLECULAR_MEDICINE_REMOVE_FILE,
  API_MOLECULAR_MEDICINE_ADD_FILES,
  API_MOLECULAR_MEDICINE_ADD_FINDING,
  API_MOLECULAR_MEDICINE_UPDATE_FINDING,
  API_MOLECULAR_MEDICINE_PATHOGENIC_FINDINGS,
  API_MOLECULAR_MEDICINE_DELETE_FINDING,
  API_MOLECULAR_MEDICINE_FILTER_TYPE,
  API_MOLECULAR_MEDICINE_FILTER_STATUS,
  API_MOLECULAR_MEDICINE_FILTER_CLINICAL_AREA,
  API_MOLECULAR_MEDICINE_FILTER_PRIORITY,
  API_MOLECULAR_MEDICINE_REPORT_DATE_RANGE,
  API_MOLECULAR_MEDICINE_REPORT_PROCESSING_TIMES,
  API_MOLECULAR_MEDICINE_FORM_OPTIONS,
  API_MOLECULAR_MEDICINE_VALIDATE_GENE
} from "../../../helpers/urlHelper";

// ========================================================================
// 🧬 SERVICIO DE MEDICINA MOLECULAR
// ========================================================================

// Configuración base de axios
const api = axios.create({
  baseURL: API_BASE_MOLECULAR_MEDICINE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para manejo de errores
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('Error en API de Medicina Molecular:', error);
    return Promise.reject(error);
  }
);

// ========================================================================
// OPERACIONES CRUD BÁSICAS
// ========================================================================

/**
 * Obtener todos los estudios de medicina molecular con filtros opcionales
 * @param {Object} params - Parámetros de filtrado
 * @returns {Promise} Lista de estudios
 */
export const getAllMolecularMedicineStudies = async (params = {}) => {
  try {
    const response = await api.get(API_MOLECULAR_MEDICINE_LIST, { params });
    return response.data;
  } catch (error) {
    throw new Error(`Error al obtener estudios: ${error.message}`);
  }
};

/**
 * Obtener un estudio específico por ID
 * @param {string} id - ID del estudio
 * @returns {Promise} Datos del estudio
 */
export const getMolecularMedicineStudyById = async (id) => {
  try {
    const response = await api.get(`${API_MOLECULAR_MEDICINE_DETAILS}/${id}`);
    return response.data;
  } catch (error) {
    throw new Error(`Error al obtener estudio ${id}: ${error.message}`);
  }
};

/**
 * Crear un nuevo estudio de medicina molecular
 * @param {FormData} formData - Datos del estudio (incluye archivos)
 * @returns {Promise} Estudio creado
 */
export const createMolecularMedicineStudy = async (formData) => {
  try {
    const response = await api.post(API_MOLECULAR_MEDICINE_CREATE, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(`Error al crear estudio: ${error.message}`);
  }
};

/**
 * Actualizar un estudio existente
 * @param {string} id - ID del estudio
 * @param {FormData} formData - Datos actualizados
 * @returns {Promise} Estudio actualizado
 */
export const updateMolecularMedicineStudy = async (id, formData) => {
  try {
    const response = await api.put(`${API_MOLECULAR_MEDICINE_UPDATE}/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(`Error al actualizar estudio ${id}: ${error.message}`);
  }
};

/**
 * Eliminar un estudio
 * @param {string} id - ID del estudio
 * @returns {Promise} Confirmación de eliminación
 */
export const deleteMolecularMedicineStudy = async (id) => {
  try {
    const response = await api.delete(`${API_MOLECULAR_MEDICINE_DELETE}/${id}`);
    return response.data;
  } catch (error) {
    throw new Error(`Error al eliminar estudio ${id}: ${error.message}`);
  }
};

// ========================================================================
// ESTADÍSTICAS Y CASOS URGENTES
// ========================================================================

/**
 * Obtener estadísticas generales
 * @returns {Promise} Estadísticas del sistema
 */
export const getMolecularMedicineStats = async () => {
  try {
    const response = await api.get(API_MOLECULAR_MEDICINE_STATS);
    return response.data;
  } catch (error) {
    throw new Error(`Error al obtener estadísticas: ${error.message}`);
  }
};

/**
 * Obtener casos urgentes
 * @returns {Promise} Lista de casos urgentes
 */
export const getUrgentCases = async () => {
  try {
    const response = await api.get(API_MOLECULAR_MEDICINE_URGENT);
    return response.data;
  } catch (error) {
    throw new Error(`Error al obtener casos urgentes: ${error.message}`);
  }
};

// ========================================================================
// BÚSQUEDAS ESPECÍFICAS
// ========================================================================

/**
 * Buscar estudios por gen específico
 * @param {string} gene - Nombre del gen
 * @returns {Promise} Estudios relacionados con el gen
 */
export const searchByGene = async (gene) => {
  try {
    const response = await api.get(`${API_MOLECULAR_MEDICINE_SEARCH_GENE}/${gene}`);
    return response.data;
  } catch (error) {
    throw new Error(`Error al buscar por gen ${gene}: ${error.message}`);
  }
};

/**
 * Obtener estudios por paciente específico
 * @param {string} patientId - ID del paciente
 * @returns {Promise} Estudios del paciente
 */
export const getStudiesByPatient = async (patientId) => {
  try {
    const response = await api.get(`${API_MOLECULAR_MEDICINE_BY_PATIENT}/${patientId}`);
    return response.data;
  } catch (error) {
    throw new Error(`Error al obtener estudios del paciente ${patientId}: ${error.message}`);
  }
};

// ========================================================================
// GESTIÓN DE ESTADOS
// ========================================================================

/**
 * Actualizar solo el estado del estudio
 * @param {string} id - ID del estudio
 * @param {string} status - Nuevo estado
 * @returns {Promise} Confirmación de actualización
 */
export const updateStudyStatus = async (id, status) => {
  try {
    const response = await api.patch(`${API_MOLECULAR_MEDICINE_STATUS}/${id}/status`, { status });
    return response.data;
  } catch (error) {
    throw new Error(`Error al actualizar estado del estudio ${id}: ${error.message}`);
  }
};

// ========================================================================
// GESTIÓN DE ARCHIVOS
// ========================================================================

/**
 * Eliminar archivo específico del estudio
 * @param {string} studyId - ID del estudio
 * @param {string} fileId - ID del archivo
 * @returns {Promise} Confirmación de eliminación
 */
export const removeFile = async (studyId, fileId) => {
  try {
    const response = await api.delete(`${API_MOLECULAR_MEDICINE_REMOVE_FILE}/${studyId}/files/${fileId}`);
    return response.data;
  } catch (error) {
    throw new Error(`Error al eliminar archivo: ${error.message}`);
  }
};

/**
 * Agregar archivos adicionales a un estudio existente
 * @param {string} studyId - ID del estudio
 * @param {FormData} formData - Archivos y metadatos
 * @returns {Promise} Confirmación de adición
 */
export const addFilesToStudy = async (studyId, formData) => {
  try {
    const response = await api.post(`${API_MOLECULAR_MEDICINE_ADD_FILES}/${studyId}/files`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(`Error al agregar archivos: ${error.message}`);
  }
};

// ========================================================================
// GESTIÓN DE HALLAZGOS GENÉTICOS
// ========================================================================

/**
 * Agregar hallazgo genético
 * @param {string} studyId - ID del estudio
 * @param {Object} findingData - Datos del hallazgo
 * @returns {Promise} Hallazgo agregado
 */
export const addFinding = async (studyId, findingData) => {
  try {
    const response = await api.post(`${API_MOLECULAR_MEDICINE_ADD_FINDING}/${studyId}/findings`, findingData);
    return response.data;
  } catch (error) {
    throw new Error(`Error al agregar hallazgo: ${error.message}`);
  }
};

/**
 * Actualizar hallazgo genético específico
 * @param {string} studyId - ID del estudio
 * @param {string} findingId - ID del hallazgo
 * @param {Object} findingData - Datos actualizados
 * @returns {Promise} Hallazgo actualizado
 */
export const updateFinding = async (studyId, findingId, findingData) => {
  try {
    const response = await api.put(`${API_MOLECULAR_MEDICINE_UPDATE_FINDING}/${studyId}/findings/${findingId}`, findingData);
    return response.data;
  } catch (error) {
    throw new Error(`Error al actualizar hallazgo: ${error.message}`);
  }
};

/**
 * Obtener solo hallazgos patogénicos de un estudio
 * @param {string} studyId - ID del estudio
 * @returns {Promise} Hallazgos patogénicos
 */
export const getPathogenicFindings = async (studyId) => {
  try {
    const response = await api.get(`${API_MOLECULAR_MEDICINE_PATHOGENIC_FINDINGS}/${studyId}/findings/pathogenic`);
    return response.data;
  } catch (error) {
    throw new Error(`Error al obtener hallazgos patogénicos: ${error.message}`);
  }
};

/**
 * Eliminar hallazgo genético específico
 * @param {string} studyId - ID del estudio
 * @param {string} findingId - ID del hallazgo
 * @returns {Promise} Confirmación de eliminación
 */
export const deleteFinding = async (studyId, findingId) => {
  try {
    const response = await api.delete(`${API_MOLECULAR_MEDICINE_DELETE_FINDING}/${studyId}/findings/${findingId}`);
    return response.data;
  } catch (error) {
    throw new Error(`Error al eliminar hallazgo: ${error.message}`);
  }
};

// ========================================================================
// FILTROS AVANZADOS
// ========================================================================

/**
 * Filtrar por tipo de estudio
 * @param {string} type - Tipo de estudio
 * @returns {Promise} Estudios filtrados
 */
export const getStudiesByType = async (type) => {
  try {
    const response = await api.get(`${API_MOLECULAR_MEDICINE_FILTER_TYPE}/${type}`);
    return response.data;
  } catch (error) {
    throw new Error(`Error al filtrar por tipo ${type}: ${error.message}`);
  }
};

/**
 * Filtrar por estado
 * @param {string} status - Estado del estudio
 * @returns {Promise} Estudios filtrados
 */
export const getStudiesByStatus = async (status) => {
  try {
    const response = await api.get(`${API_MOLECULAR_MEDICINE_FILTER_STATUS}/${status}`);
    return response.data;
  } catch (error) {
    throw new Error(`Error al filtrar por estado ${status}: ${error.message}`);
  }
};

/**
 * Filtrar por área clínica
 * @param {string} area - Área clínica
 * @returns {Promise} Estudios filtrados
 */
export const getStudiesByClinicalArea = async (area) => {
  try {
    const response = await api.get(`${API_MOLECULAR_MEDICINE_FILTER_CLINICAL_AREA}/${area}`);
    return response.data;
  } catch (error) {
    throw new Error(`Error al filtrar por área clínica ${area}: ${error.message}`);
  }
};

/**
 * Filtrar por prioridad
 * @param {string} priority - Prioridad del estudio
 * @returns {Promise} Estudios filtrados
 */
export const getStudiesByPriority = async (priority) => {
  try {
    const response = await api.get(`${API_MOLECULAR_MEDICINE_FILTER_PRIORITY}/${priority}`);
    return response.data;
  } catch (error) {
    throw new Error(`Error al filtrar por prioridad ${priority}: ${error.message}`);
  }
};

// ========================================================================
// REPORTES
// ========================================================================

/**
 * Generar reporte de estudios por rango de fechas
 * @param {Object} params - Parámetros del reporte
 * @returns {Promise} Reporte generado
 */
export const getDateRangeReport = async (params) => {
  try {
    const response = await api.get(API_MOLECULAR_MEDICINE_REPORT_DATE_RANGE, { params });
    return response.data;
  } catch (error) {
    throw new Error(`Error al generar reporte por fechas: ${error.message}`);
  }
};

/**
 * Reporte de tiempos de procesamiento
 * @returns {Promise} Reporte de tiempos
 */
export const getProcessingTimesReport = async () => {
  try {
    const response = await api.get(API_MOLECULAR_MEDICINE_REPORT_PROCESSING_TIMES);
    return response.data;
  } catch (error) {
    throw new Error(`Error al generar reporte de tiempos: ${error.message}`);
  }
};

// ========================================================================
// UTILIDADES
// ========================================================================

/**
 * Obtener opciones para formularios (enums)
 * @returns {Promise} Opciones disponibles
 */
export const getFormOptions = async () => {
  try {
    const response = await api.get(API_MOLECULAR_MEDICINE_FORM_OPTIONS);
    return response.data;
  } catch (error) {
    throw new Error(`Error al obtener opciones de formulario: ${error.message}`);
  }
};

/**
 * Validar gen
 * @param {string} gene - Nombre del gen a validar
 * @returns {Promise} Resultado de validación
 */
export const validateGene = async (gene) => {
  try {
    const response = await api.get(`${API_MOLECULAR_MEDICINE_VALIDATE_GENE}/${gene}`);
    return response.data;
  } catch (error) {
    throw new Error(`Error al validar gen ${gene}: ${error.message}`);
  }
};

// ========================================================================
// FUNCIONES DE UTILIDAD ADICIONALES
// ========================================================================

/**
 * Función auxiliar para crear FormData con archivos
 * @param {Object} data - Datos del formulario
 * @param {FileList} files - Lista de archivos
 * @returns {FormData} FormData preparado
 */
export const createFormDataWithFiles = (data, files = null) => {
  const formData = new FormData();
  
  // Agregar datos del formulario
  Object.keys(data).forEach(key => {
    if (data[key] !== null && data[key] !== undefined) {
      if (typeof data[key] === 'object' && !Array.isArray(data[key])) {
        formData.append(key, JSON.stringify(data[key]));
      } else {
        formData.append(key, data[key]);
      }
    }
  });
  
  // Agregar archivos si existen
  if (files && files.length > 0) {
    Array.from(files).forEach(file => {
      formData.append('files', file);
    });
  }
  
  return formData;
};

/**
 * Función auxiliar para manejar respuestas de error
 * @param {Error} error - Error capturado
 * @returns {Object} Error formateado
 */
export const handleApiError = (error) => {
  const errorMessage = error.response?.data?.message || error.message || 'Error desconocido';
  const errorCode = error.response?.status || 500;
  
  return {
    message: errorMessage,
    code: errorCode,
    details: error.response?.data || null
  };
};

// Exportar todas las funciones como default también para mayor flexibilidad
export default {
  // CRUD básico
  getAllMolecularMedicineStudies,
  getMolecularMedicineStudyById,
  createMolecularMedicineStudy,
  updateMolecularMedicineStudy,
  deleteMolecularMedicineStudy,
  
  // Estadísticas y urgentes
  getMolecularMedicineStats,
  getUrgentCases,
  
  // Búsquedas
  searchByGene,
  getStudiesByPatient,
  
  // Estados
  updateStudyStatus,
  
  // Archivos
  removeFile,
  addFilesToStudy,
  
  // Hallazgos
  addFinding,
  updateFinding,
  getPathogenicFindings,
  deleteFinding,
  
  // Filtros
  getStudiesByType,
  getStudiesByStatus,
  getStudiesByClinicalArea,
  getStudiesByPriority,
  
  // Reportes
  getDateRangeReport,
  getProcessingTimesReport,
  
  // Utilidades
  getFormOptions,
  validateGene,
  createFormDataWithFiles,
  handleApiError
};